import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import {
  loadOfferDecisionSubsequentConditions,
  updateOfferDecisionSubsequentConditions
} from 'actions/actionCreators/offer/offerDecisionActions';
import memoize from 'lodash.memoize';
import { when, valueChanged } from 'utils/fn-combinators';
import { CollapseCard } from '@totalsoft/rocket-ui';
import WrappedFakeText from 'components/common/WrappedFakeText';
import Ballot from '@mui/icons-material/Ballot';
import OfferDecisionSubsequentConditionList from './OfferDecisionSubsequentConditionList';
import * as editableDocument from 'utils/model/editableDocument';
import { collection } from 'utils/immutable';

class OfferDecisionSubsequentConditionContainer extends PureComponent {
  state = {
    loading: true,
    saving: false,
    prevPropsSubsequentConditions: null,
    subsequentConditions: null
  };

  static getDerivedStateFromProps(props, state) {
    if (props.subsequentConditions !== state.prevPropsSubsequentConditions) {
      return {
        prevPropsSubsequentConditions: props.subsequentConditions,
        subsequentConditions: props.subsequentConditions.map(editableDocument.create),
        loading: false
      };
    }

    return null;
  }

  componentDidUpdate() {
    if (!this.props.subsequentConditions.isLoaded) {
      this.loadData();
    }
  }

  componentDidMount() {
    this.loadData().then(() => {
      this.setState({ loading: false });
    });
  }

  loadData = async () => {
    const { documentId, siteId } = this.props;

    await this.props.actions.loadOfferDecisionSubsequentConditions(documentId, siteId);
  };

  static getItemBusinessRules = ({ userProfile }) => ({
    user: when(
      valueChanged(condition => condition.active),
      userProfile.name
    )
  });

  changeItemProperty = memoize(index => (prop, value) => {
    this.setState(prevState => ({
      subsequentConditions: collection.update(
        index,
        editableDocument.update(prop, value, OfferDecisionSubsequentConditionContainer.getItemBusinessRules(this.props)),
        prevState.subsequentConditions
      )
    }));
  });

  save = async () => {
    const { actions } = this.props;

    this.setState({
      saving: true
    });

    try {
      const event = await actions.updateOfferDecisionSubsequentConditions(
        this.props.documentId,
        this.props.siteId,
        this.state.subsequentConditions.map(x => x.document)
      );

      if (event.ok) {
        this.setState(prevState => ({
          subsequentConditions: prevState.subsequentConditions.map(editableDocument.resetDirtyInfo)
        }));
      }
    } catch (error) {
      // continue regardless of error
    }
    this.setState({ saving: false });
  };

  render() {
    const { subsequentConditions, loading, saving } = this.state;
    const { t } = this.props;

    return (
      <CollapseCard
        icon={Ballot}
        iconColor='primary'
        title={t('OfferEdit.Decision.SubsequentCondition.Title')}
        toggleOnHeaderClick
        content={
          loading ? (
            <WrappedFakeText lines={6} />
          ) : (
            <OfferDecisionSubsequentConditionList
              subsequentConditions={subsequentConditions}
              onPropertyChange={this.changeItemProperty}
              onSave={this.save}
              saving={saving}
            />
          )
        }
      />
    );
  }
}

OfferDecisionSubsequentConditionContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  subsequentConditions: PropTypes.array.isRequired,
  userProfile: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  let userProfile;
  if (state.oidc.user && state.oidc.user.profile) userProfile = state.oidc.user.profile;
  return {
    subsequentConditions: state.currentOffer.decision.subsequentConditions,
    userProfile: userProfile
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      loadOfferDecisionSubsequentConditions,
      updateOfferDecisionSubsequentConditions
    },
    dispatch
  )
});

export default compose(connect(mapStateToProps, mapDispatchToProps), withTranslation())(OfferDecisionSubsequentConditionContainer);
